import { Injectable } from "@angular/core";
import { BehaviorSubject, firstValueFrom, from, Observable } from "rxjs";
import { DeviceDataExportService } from "../device-data-export/brian-device-export.service";
import { StructureService } from "../http-api/structure.service";
import { UnzipResult } from "../device-data-export/unzip-result";
import { ExternalSyncUploadService } from "../image-upload/external-sync-upload.service";
import { FileSystemAccessService } from "../file-system-access.service";
import { INSPECTION_STRUCTURE_FORMAT } from "../../_utils/inspection-structure.utils";

@Injectable({
  providedIn: "root"
})
export class BrianSynchronizedInspectionStateService {
  private brianSynchronizedInspectionIdSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private deviceDataExportService: DeviceDataExportService,
    private structureService: StructureService,
    private externalSyncUploadService: ExternalSyncUploadService,
    private fileSystemAccessService: FileSystemAccessService
  ) {}

  async setBrianSynchronizedInspectionId(id: string | null, title: string): Promise<UnzipResult> {
    this.brianSynchronizedInspectionIdSubject.next(id);

    if (id) {
      try {
        const synchronisedData = await this.startSynchronization(id, title);
        await firstValueFrom(this.fileSystemAccessService.executeReturnAppBatFile());
        return synchronisedData;
      } catch (error) {
        console.error("An error occurred when starting synchronisation :", error);
        return { error: "An error occurred when starting synchronisation." };
      }
    } else {
      // if no id means synchronization was deactivated, return empty result
      this.externalSyncUploadService.stopCurrentProccessingFiles();
      return {};
    }
  }

  private async startSynchronization(id: string, title: string) {
    // IPLEX One gets the tree layout: nested folders named after the asset display names.
    // An inspection folder that already exists under Monitor is kept as is, so legacy folders on the device stay valid.
    const blob = await firstValueFrom(this.structureService.getStructureZip(null, id, false, INSPECTION_STRUCTURE_FORMAT.TREE));

    const result: UnzipResult = await this.deviceDataExportService.exportSynchronizedInspectionTemplateToBrian(blob, id, title);
    await firstValueFrom(this.deviceDataExportService.exportPresetDataToDevice());
    if (result.error) {
      console.error("Export Error:", result.error);
      return result;
    } else {
      this.externalSyncUploadService.synchronizeFiles(id, result.path);
    }

    return result;
  }

  getBrianSynchronizedInspectionId$(): Observable<string | null> {
    return this.brianSynchronizedInspectionIdSubject.asObservable();
  }
}
